import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { Modal, message } from "antd";
import { HiOutlineCamera, HiOutlineLocationMarker } from "react-icons/hi";
import InfyButton from "../../InfyButton/InfyButton";
import { GeoPictureValue } from "../helper";
import PermissionHelpSteps from "./PermissionHelpSteps";
import styles from "./roundedModal.module.scss";

interface GeoCameraModalProps {
  open: boolean;
  onCancel: () => void;
  /** Uploads the captured frame and resolves to its stored URL. */
  upload: (blob: Blob, filename: string) => Promise<string>;
  onCaptured: (value: GeoPictureValue) => void;
}

export const GEOLOCATION_OPTIONS: PositionOptions = { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 };

const getCurrentPosition = (): Promise<GeolocationPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device cannot share its location."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, GEOLOCATION_OPTIONS);
  });

/**
 * "denied" and "unavailable" need different messages and different affordances: a blocked permission
 * is fixed in browser settings and retrying is pointless, while a timeout or a lost fix usually
 * succeeds on a second attempt. Collapsing both into one failure state told the learner to change a
 * setting that was never the problem.
 */
type LocationState = "locating" | "ready" | "denied" | "unavailable";

const LOCATION_MESSAGES: Record<Exclude<LocationState, "ready">, string> = {
  locating: "Getting your location…",
  denied: "Location is blocked for this site. Allow it in your browser's site settings, then reopen the camera — the photo cannot be saved without it.",
  unavailable: "Could not get your location. Move somewhere with a clearer signal and try again."
};

/**
 * In-page camera for GEO_PICTURE answers.
 *
 * Deliberately `getUserMedia` rather than `<input capture>`: `capture` is only a hint, and a desktop
 * browser ignores it and opens a file picker instead — which would let a learner answer with any
 * image on disk. Painting a live frame to a canvas is the only path with no file-picker fallback, so
 * a device with no camera (or a denied permission) cannot answer this question at all. That is the
 * intended consequence of the photo having to be taken, not chosen.
 *
 * Needs a secure context: `getUserMedia` is unavailable over plain http except on localhost.
 */
const GeoCameraModal: FunctionComponent<GeoCameraModalProps> = ({ open, onCancel, upload, onCaptured }) => {
  const previewRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Bumped every time the modal opens and again when it closes or unmounts. A capture remembers the
  // value it started under and drops its result if the value has since changed.
  const sessionRef = useRef(0);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [locationState, setLocationState] = useState<LocationState>("locating");
  const [busy, setBusy] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  // Only a refused camera is fixed in the browser settings; a missing or busy one is not, so it gets no steps.
  const [cameraRefused, setCameraRefused] = useState(false);

  /**
   * `isActive` is passed rather than captured so a retry triggered after the effect has re-run still
   * knows whether this modal is the one on screen.
   */
  const acquireLocation = useCallback((isActive: () => boolean = () => true) => {
    setLocationState("locating");

    getCurrentPosition()
      .then(() => {
        if (!isActive()) return;
        setLocationState("ready");
      })
      .catch((error: GeolocationPositionError) => {
        if (!isActive()) return;
        // Code 1 is PERMISSION_DENIED; 2 and 3 are a missing fix and a timeout, both worth retrying.
        setLocationState(error?.code === 1 ? "denied" : "unavailable");
      });
  }, []);

  // Acquire the rear camera and the device location while the modal is open; release on close.
  useEffect(() => {
    sessionRef.current += 1;
    if (!open) return undefined;

    let active = true;
    setCameraError(null);
    setCameraRefused(false);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser cannot open the camera. Please use a device with a camera.");
      return undefined;
    }

    navigator.mediaDevices
      // `ideal` rather than `exact` so a laptop with only a front camera still opens instead of failing.
      .getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false })
      .then(s => {
        if (!active) {
          s.getTracks().forEach(track => track.stop());
          return;
        }
        streamRef.current = s;
        setStream(s);
      })
      .catch((error: DOMException) => {
        if (!active) return;
        const refused = error?.name === "NotAllowedError" || error?.name === "SecurityError";
        setCameraRefused(refused);
        setCameraError(
          refused
            ? "Unable to access the camera. Please allow camera permission and try again."
            : "Unable to open the camera. Check that it is connected and not in use by another app."
        );
      });

    acquireLocation(() => active);

    return () => {
      active = false;
      sessionRef.current += 1;
      setBusy(false);
      streamRef.current?.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
      setLocationState("locating");
    };
    // `acquireLocation` is stable, so only `open` drives this.
  }, [open, acquireLocation]);

  useEffect(() => {
    if (previewRef.current && stream && previewRef.current.srcObject !== stream) {
      previewRef.current.srcObject = stream;
    }
  }, [stream]);

  const grabFrame = (): Promise<Blob> =>
    new Promise((resolve, reject) => {
      const video = previewRef.current;
      if (!video || !video.videoWidth) {
        reject(new Error("The camera is not ready yet. Please wait a moment and try again."));
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Could not capture the photo. Please try again."));
        return;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error("Could not capture the photo. Please try again."))), "image/jpeg", 0.9);
    });

  const handleCapture = async () => {
    const session = sessionRef.current;
    const isCurrent = () => sessionRef.current === session;
    setBusy(true);
    try {
      // The frame and the time are taken at the moment the learner presses Capture, before any
      // await: the location read below can take seconds, and the photo must show the scene and the
      // time of the press, not of whenever the fix or the upload finished.
      const capturedAt = new Date().toISOString();
      const framePromise = grabFrame();
      // Handled below; this keeps a frame failure from being reported as unhandled while the fix is pending.
      framePromise.catch(() => undefined);

      // Re-read the location rather than trusting the fix taken when the modal opened — the learner
      // may have moved while granting permission or framing the shot. If it fails the grabbed frame
      // is discarded, so a photo is never stored without the coordinates that go with it.
      const position = await getCurrentPosition().catch((error: GeolocationPositionError) => {
        if (isCurrent()) setLocationState(error?.code === 1 ? "denied" : "unavailable");
        throw new Error(LOCATION_MESSAGES[error?.code === 1 ? "denied" : "unavailable"]);
      });
      if (!isCurrent()) return;
      const blob = await framePromise;
      if (!isCurrent()) return;
      const url = await upload(blob, `geo-capture-${Date.now()}.jpg`);
      if (!isCurrent()) return;

      onCaptured({
        url,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        // Kept so a reviewer can tell a GPS fix from a wifi estimate: `accuracy` is the radius of
        // confidence in metres, and `altitude` is null whenever the fix did not come from GPS.
        accuracy: position.coords.accuracy ?? null,
        altitude: position.coords.altitude ?? null,
        capturedAt
      });
    } catch (err) {
      if (!isCurrent()) return;
      message.error((err as GeolocationPositionError | Error)?.message || "Could not capture the photo. Please try again.");
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const captureLabel = (() => {
    if (!stream) return "Starting camera…";
    if (locationState === "locating") return "Waiting for location…";
    if (locationState !== "ready") return "Location required";
    return "Capture photo";
  })();

  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered destroyOnClose title="Capture photo" className={styles.roundedModal}>
      <div className="flex flex-col gap-4">
        {cameraError ? (
          <div className="flex flex-col gap-3">
            <span className="text-sm text-error-1">{cameraError}</span>
            {cameraRefused && <PermissionHelpSteps />}
          </div>
        ) : (
          <div className="relative mx-auto w-full max-h-[60vh] overflow-hidden rounded-xl bg-black" style={{ aspectRatio: "3 / 4" }}>
            <video ref={previewRef} className="h-full w-full object-cover" autoPlay playsInline muted />
          </div>
        )}

        {locationState !== "ready" && (
          <div className={`flex gap-2 rounded-lg p-3 text-xs ${locationState === "locating" ? "bg-neutral-3 text-neutral-9" : "bg-error-2 text-error-1"}`}>
            <HiOutlineLocationMarker className="mt-0.5 flex-shrink-0 text-sm" />
            <div className="flex flex-col items-start gap-1">
              <span>{LOCATION_MESSAGES[locationState]}</span>
              {locationState === "denied" && <PermissionHelpSteps />}
              {locationState === "unavailable" && (
                <button type="button" onClick={() => acquireLocation()} className="cursor-pointer border-0 bg-transparent p-0 underline">
                  Try again
                </button>
              )}
            </div>
          </div>
        )}

        {!cameraError && (
          // The location gate is on the capture button rather than on submission: without it the learner frames
          // a shot, presses capture and only then learns it cannot be saved.
          <InfyButton type="primary" bicolor="primary" onClick={handleCapture} disabled={!stream || busy || locationState !== "ready"} loading={busy}>
            <HiOutlineCamera className="mr-2 inline text-base" />
            {captureLabel}
          </InfyButton>
        )}
      </div>
    </Modal>
  );
};

export default GeoCameraModal;
